import { agregerMargeEncaissee, agregerMargeFacturee, mouvementsMargeEncaisseeInterclub, mouvementsMargeFactureeInterclub } from './interclubMargin';

export const CATEGORIES_INTERCLUB_DIRECTES = ['CLIENT', 'DEVIS', 'COMMANDE', 'FACTURE', 'AVOIR'];

export function joursCalendairesDepuis(dateValeur, maintenant = new Date()) {
  if (!dateValeur) return null;
  const date = new Date(dateValeur);
  if (Number.isNaN(date.valueOf())) return null;
  const jourLocal = (valeur) => Date.UTC(valeur.getFullYear(), valeur.getMonth(), valeur.getDate());
  return Math.max(0, Math.floor((jourLocal(maintenant) - jourLocal(date)) / 86400000));
}

export function presentationMajInterclub(dateValeur, maintenant = new Date()) {
  const age = joursCalendairesDepuis(dateValeur, maintenant);
  if (age == null) return { age: null, ton: 'danger', texte: 'M.A.J Interclub : jamais' };
  const date = new Date(dateValeur);
  const texte = `M.A.J Interclub : ${date.toLocaleDateString('fr-FR')} ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  return { age, ton: age === 0 ? 'success' : age > 7 ? 'danger' : 'normal', texte };
}

export function miseAJourGlobaleReussie(execution) {
  const attendues = Array.isArray(execution?.categories_prevues) ? execution.categories_prevues : CATEGORIES_INTERCLUB_DIRECTES;
  const resultats = execution?.categories_resultats && typeof execution.categories_resultats === 'object' ? execution.categories_resultats : {};
  return execution?.circuit === 'DIRECTE'
    && execution?.statut === 'TERMINEE'
    && attendues.length > 0
    && attendues.every((categorie) => resultats[categorie]?.statut === 'SUCCES');
}

export function conserverDerniereReussite(ancienneDate, execution) {
  return miseAJourGlobaleReussie(execution) ? (execution.date_application || execution.updated_at || ancienneDate) : ancienneDate;
}

const normaliserCommercial = (valeur) => String(valeur || '').trim().toLocaleLowerCase('fr-FR');

function commandesParNumero(documents) {
  return new Map((documents || [])
    .filter((document) => document?.type_document === 'COMMANDE')
    .map((document) => [
      String(document?.donnees?.numero_commande || document?.identifiant_source || '').trim(),
      document,
    ])
    .filter(([numero]) => numero));
}

const numeroCommandeDocument = (document, factures = null) => {
  const direct = String(document?.donnees?.numero_commande || '').trim();
  if (direct) return direct;
  const numeroFacture = String(document?.donnees?.numero_facture || '').trim().toUpperCase();
  return String(factures?.get(numeroFacture)?.donnees?.numero_commande || '').trim();
};

function documentDansPerimetreCommercial(document, commandes, commercialCommande, factures = null) {
  const commercial = normaliserCommercial(commercialCommande);
  if (!commercial || ['tous', 'toute l’équipe', "toute l'équipe", "toute l'equipe", 'toute l’équipe commerciale'].includes(commercial)) return true;
  const numeroCommande = numeroCommandeDocument(document, factures);
  const commande = commandes.get(numeroCommande);
  const commercialDocument = normaliserCommercial(commande?.donnees?.commercial_commande) || 'non attribué';
  return commercialDocument === commercial;
}

function documentDansPerimetreClub(document, codesFiltres, options = {}) {
  const code = String(document?.donnees?.code_client || '').trim();
  if (!codesFiltres.size) return true;
  const codesConnus = options.codesClientsConnus instanceof Set ? options.codesClientsConnus : codesFiltres;
  if (!codesConnus.has(code)) return options.inclureNonRapproches === true;
  return codesFiltres.has(code);
}

function statutRapprochement(document, options = {}) {
  const code = String(document?.donnees?.code_client || '').trim();
  const codesConnus = options.codesClientsConnus instanceof Set ? options.codesClientsConnus : null;
  return codesConnus?.has(code) ? 'RAPPROCHE' : 'NON_RAPPROCHE';
}

export function mouvementsCaFactureInterclub(documents, codesClients, options = {}) {
  const codes = codesClients instanceof Set ? codesClients : new Set(codesClients || []);
  const commandes = commandesParNumero(documents);
  const uniques = new Map();
  for (const document of documents || []) {
    if (!['FACTURE', 'AVOIR'].includes(document?.type_document)) continue;
    if (!documentDansPerimetreClub(document, codes, options)) continue;
    if (!documentDansPerimetreCommercial(document, commandes, options.commercialCommande)) continue;
    const identifiant = String(document?.identifiant_source || '').trim();
    if (!identifiant) continue;
    const cle = `${document.type_document}|${identifiant}`;
    const montant = Number(document?.donnees?.montant_ttc || 0);
    const montantHt = Number(document?.donnees?.montant_ht || 0);
    const numeroCommande = numeroCommandeDocument(document);
    const commande = commandes.get(numeroCommande);
    uniques.set(cle, {
      typeDocument: document.type_document,
      date: document?.donnees?.date_facturation || null,
      montantTTC: document.type_document === 'AVOIR' ? -Math.abs(montant) : montant,
      montantHT: document.type_document === 'AVOIR' ? -Math.abs(montantHt) : montantHt,
      identifiant,
      numeroCommande,
      codeClient: String(document?.donnees?.code_client || '').trim(),
      clientInterclub: String(document?.donnees?.nom_client || '').trim(),
      commercialCommande: String(commande?.donnees?.commercial_commande || '').trim(),
      rapprochementClub: statutRapprochement(document, options),
    });
  }
  // Les retours certifiés sont des mouvements datés autonomes. Ils ne doivent
  // pas être rattachés artificiellement à une facture pour réduire le CA.
  for (const document of documents || []) {
    const donnees = document?.donnees || {};
    const type = String(donnees.type_mouvement || '').trim().toUpperCase();
    if (document?.type_document !== 'MARGE_FACTUREE' || !['RETOUR', 'AUTRE_CORRECTION_CERTIFIEE'].includes(type)) continue;
    if (!documentDansPerimetreClub(document, codes, options)) continue;
    if (!documentDansPerimetreCommercial(document, commandes, options.commercialCommande)) continue;
    const identifiant = String(donnees.numero_document || document?.identifiant_source || document?.id || '').trim();
    const montant = Number(donnees.ca_ttc_mouvement);
    const montantHt = Number(donnees.ca_ht_mouvement);
    if (!identifiant || !Number.isFinite(montant) || !Number.isFinite(montantHt)) continue;
    uniques.set(`${type}|${identifiant}`, {
      typeDocument: type,
      date: donnees.date_mouvement || null,
      montantTTC: Number(donnees.sens) < 0 || type === 'RETOUR' ? -Math.abs(montant) : montant,
      montantHT: Number(donnees.sens) < 0 || type === 'RETOUR' ? -Math.abs(montantHt) : montantHt,
      identifiant,
      numeroCommande: String(donnees.numero_commande || '').trim(),
      codeClient: String(donnees.code_client || '').trim(),
      clientInterclub: String(donnees.nom_client || '').trim(),
      commercialCommande: String(donnees.commercial_commande || commandes.get(String(donnees.numero_commande || '').trim())?.donnees?.commercial_commande || '').trim(),
      rapprochementClub: statutRapprochement(document, options),
    });
  }
  return [...uniques.values()];
}

export function mouvementsPaiementInterclub(documents, codesClients, options = {}) {
  const codes = codesClients instanceof Set ? codesClients : new Set(codesClients || []);
  const commandes = commandesParNumero(documents);
  const factures = new Map((documents || [])
    .filter((document) => document?.type_document === 'FACTURE')
    .map((document) => [String(document?.donnees?.numero_facture || document?.identifiant_source || '').trim().toUpperCase(), document]));
  const uniques = new Map();
  for (const document of documents || []) {
    if (document?.type_document !== 'PAIEMENT') continue;
    const identifiant = String(document?.identifiant_source || '').trim();
    if (!identifiant) continue;
    const numeroCommande = numeroCommandeDocument(document, factures);
    const commande = commandes.get(numeroCommande);
    const facture = factures.get(String(document?.donnees?.numero_facture || '').trim().toUpperCase());
    const documentClient = String(document?.donnees?.code_client || '').trim() ? document : (facture || document);
    if (!documentDansPerimetreClub(documentClient, codes, options)) continue;
    if (!documentDansPerimetreCommercial(document, commandes, options.commercialCommande, factures)) continue;
    uniques.set(identifiant, {
      typeDocument: 'PAIEMENT',
      date: document?.donnees?.date_paiement || null,
      montantTTC: Number(document?.donnees?.montant_impute ?? document?.donnees?.montant ?? 0),
      identifiant,
      numeroCommande,
      numeroFacture: String(document?.donnees?.numero_facture || '').trim(),
      codeClient: String(documentClient?.donnees?.code_client || '').trim(),
      clientInterclub: String(documentClient?.donnees?.nom_client || '').trim(),
      commercialCommande: String(commande?.donnees?.commercial_commande || '').trim(),
      rapprochementClub: statutRapprochement(documentClient, options),
    });
  }
  return [...uniques.values()];
}

export function syntheseCaInterclubV2(documents, codesClientsFiltres, options = {}) {
  const factures = mouvementsCaFactureInterclub(documents, codesClientsFiltres, options);
  const encaissements = mouvementsPaiementInterclub(documents, codesClientsFiltres, options);
  const margesFacturees = mouvementsMargeFactureeInterclub(documents, codesClientsFiltres, options);
  const margesEncaissees = mouvementsMargeEncaisseeInterclub(margesFacturees, encaissements);
  const dans = (mouvement, debut, fin) => {
    const date = String(mouvement.date || '').slice(0, 10);
    return Boolean(date) && (!debut || date >= debut) && (!fin || date <= fin);
  };
  const periode = (debut, fin) => {
    const pieces = factures.filter((item) => dans(item, debut, fin));
    const reglements = encaissements.filter((item) => dans(item, debut, fin));
    const nonRapproches = [...pieces, ...reglements].filter((item) => item.rapprochementClub === 'NON_RAPPROCHE');
    const margeFacturee = agregerMargeFacturee(margesFacturees, pieces, debut, fin);
    const margeEncaissee = agregerMargeEncaissee(margesEncaissees, debut, fin);
    return {
      caHT: pieces.reduce((total, item) => total + Number(item.montantHT || 0), 0),
      caTTC: pieces.reduce((total, item) => total + Number(item.montantTTC || 0), 0),
      encaisse: reglements.reduce((total, item) => total + Number(item.montantTTC || 0), 0),
      factures: pieces,
      encaissements: reglements,
      nonRapproches,
      margeFacturee,
      margeEncaissee,
    };
  };
  return {
    source: 'Interclub V2',
    formuleFacture: 'FACTURES - AVOIRS, selon la date de pièce et le commercial de la commande',
    formuleEncaisse: 'RÈGLEMENTS / IMPUTATIONS, selon leur date effective, rattachés à la facture puis à la commande',
    mois: periode(options.debutMois, options.finMois),
    cumul: periode(options.debutCumul, options.finCumul),
  };
}
